import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { MachinesPortalState } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { DataCollectionType } from 'lib_ts/enums/data-collection.enums';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { MachineStatus } from 'lib_ts/enums/machine.enums';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import { IAdminMachine } from 'lib_ts/interfaces/i-machine';
import { IMachineServiceLog } from 'lib_ts/interfaces/i-machine-service-log';
import { IMachineDetailsResponseMsg } from 'lib_ts/interfaces/machine-msg/i-machine-details';
import { IVisionCameraCalibration } from 'lib_ts/interfaces/vision/i-vision-camera-calibration';
import { first, isEqual } from 'lodash';
import { AdminMachinesService } from 'services/admin/machines.service';
import { DataCollectionsService } from 'services/data-collections.service';
import { MachineServiceLogsService } from 'services/machine-service-logs.service';
import { VisionCameraCalibrationsService } from 'services/vision-camera-calibrations';
import { WebSocketService } from 'services/web-socket.service';
import { StateCreator } from 'zustand';

const STORE_NAME = 'MachinesPortalStore.MachineDetailsSlice';
const MACHINE_DETAILS_TIMEOUT_MS = 10000; // responses are generally sent out every 5 seconds

/**
 * This slice is primarily responsible for managing the machine details we show in the MachineSettingsDialog.
 */
interface MachineDetailsSliceState {
  loadingMachineDetails: boolean;
  machineDetailsInitialized?: boolean;

  // machine details as shown in the dialog settings dialog
  machine: IAdminMachine | undefined;

  // last repeatability test data collection fetched from a standalone endpoint and stored separately to avoid changing the IAdminMachine interface
  lastCompletedRT: IDataCollection | undefined;
  lastCompletedVcc: IVisionCameraCalibration | undefined;

  serviceLogs: IMachineServiceLog[];

  // fetched via MachineStateSection WS listener
  lastMachineStatus?: MachineStatus;

  subscribingDetails: boolean;
  subscribedDetails: boolean;
  subscriptionDetailsLastUpdated?: string;
}

type MachineDetailsSliceAction = {
  setLoadingMachineDetails: (loading: boolean) => void;
  setMachineDetailsInitialized: (initialized: boolean) => void;

  setMachineDetails: (machine?: IAdminMachine) => void;
  resetMachineDetails: () => void;

  initMachineDetails: (machineID: string) => Promise<boolean>;
  fetchMachineDetails: (machineID: string) => Promise<boolean>;

  setLastMachineStatus: (status: MachineStatus) => void;

  startDetailsSubscription: (machineID: string) => Promise<void>;
  stopDetailsSubscription: (machineID: string) => Promise<void>;
  restartDetailsSubscription: () => Promise<void>;

  setServiceLogs: (logs: IMachineServiceLog[]) => void;
};

export type MachineDetailsSlice = MachineDetailsSliceState &
  MachineDetailsSliceAction;

type CreateMachineDetailsSlice = StateCreator<
  MachinesPortalState,
  [['zustand/devtools', never]],
  [],
  MachineDetailsSlice
>;

const initialState: MachineDetailsSliceState = {
  loadingMachineDetails: false,
  machineDetailsInitialized: false,

  machine: undefined,

  lastMachineStatus: undefined,
  lastCompletedRT: undefined,
  lastCompletedVcc: undefined,
  serviceLogs: [],

  subscribedDetails: false,
  subscribingDetails: false,
};

export const createMachineDetailsSlice: CreateMachineDetailsSlice = (
  set,
  get
) => {
  // private helpers, not available outside this scope
  const onMachineDetailsMessage = (event: CustomEvent) => {
    const data: IMachineDetailsResponseMsg = event.detail;
    const { machine: machineDetails, timestamp } = data;

    set((state) => {
      const merged = {
        ...state.machine,
        ...machineDetails,
      };

      return {
        subscribingDetails: false,
        subscribedDetails: true,
        subscriptionDetailsLastUpdated: timestamp,
        machine: isEqual(state.machine, merged) ? state.machine : merged,
      };
    });

    startDetailsTimer();
  };

  let detailsSubTimer: NodeJS.Timeout | null = null;

  const stopDetailsTimer = () => {
    if (detailsSubTimer) {
      clearTimeout(detailsSubTimer);
      detailsSubTimer = null;
    }
  };

  // if we haven't received a msg within the timeout, flag as disconnected
  const startDetailsTimer = () => {
    stopDetailsTimer();

    detailsSubTimer = setTimeout(() => {
      set({ subscribedDetails: false });
    }, MACHINE_DETAILS_TIMEOUT_MS);
  };

  return {
    ...initialState,

    setMachineDetails: (machine) => {
      set({
        machine,
      });
    },

    resetMachineDetails: () => {
      set(initialState);
    },

    setMachineDetailsInitialized: (initialized) => {
      set({
        machineDetailsInitialized: initialized,
      });
    },

    setLoadingMachineDetails: (loading) => {
      set({
        loadingMachineDetails: loading,
      });
    },

    // checks to see if we already have machine details before fetching
    initMachineDetails: async (machineID) => {
      const { machines, machineDetailsInitialized, fetchMachineDetails } =
        get();

      // prevents redundant fetches if the fetch is in progress or already complete
      if (machineDetailsInitialized) {
        return true;
      }

      set({
        machineDetailsInitialized: true,
      });

      const machine = machines.find((m) => m.machineID === machineID);

      // we already have partial machine details from the Machines List
      // show whatever we have while we fetch the full view
      if (machine) {
        set({
          machine,
        });
      }

      set({
        loadingMachineDetails: true,
      });

      // grab the full up-to-date machine details from the API
      const res = await fetchMachineDetails(machineID);

      if (!res) {
        set({
          loadingMachineDetails: false,
          machineDetailsInitialized: false,
        });
        return false;
      }

      set({
        loadingMachineDetails: false,
      });
      return true;
    },

    startDetailsSubscription: async (machineID) => {
      WebSocketService.send(
        WsMsgType.U2S_MachineDetails,
        { enable: true, machineID: machineID },
        STORE_NAME
      );

      set({
        subscribingDetails: true,
      });

      WebSocketHelper.on(WsMsgType.S2U_MachineDetails, onMachineDetailsMessage);

      startDetailsTimer();
    },

    stopDetailsSubscription: async (machineID) => {
      WebSocketService.send(
        WsMsgType.U2S_MachineDetails,
        { enable: false, machineID },
        STORE_NAME
      );

      WebSocketHelper.remove(
        WsMsgType.S2U_MachineDetails,
        onMachineDetailsMessage
      );

      stopDetailsTimer();

      set({
        subscribingDetails: false,
        subscribedDetails: false,
      });
    },

    restartDetailsSubscription: async () => {
      // useMachineSettingsDialogState useEffect will handle re-initting the subscription after the below
      set({
        machineDetailsInitialized: false,
      });
    },

    fetchMachineDetails: async (machineID) => {
      const { machine: currentMachine } = get();

      const machineDetails =
        await AdminMachinesService.getInstance().getDetails(machineID);

      // handle race condition where the machineID has changed before the response is received
      // e.g. if a user closes the MachineSettingsDialog and quickly opens another one with a different machineID
      if (
        currentMachine &&
        currentMachine.machineID !== machineDetails?.machineID
      ) {
        // we no longer need this machine's details, don't update the store
        return false;
      }

      if (machineDetails) {
        const lastCompletedRT = first(
          await DataCollectionsService.findLast({
            machineID: machineDetails.machineID,
            ball_type: machineDetails.ball_type,
            type: DataCollectionType.RepeatabilityTest,
            complete: true,
          })
        );

        const serviceLogs =
          await MachineServiceLogsService.getInstance().getAll(
            machineDetails.machineID
          );

        // fetch last completed vision calibration if applicable
        const onVision =
          machineDetails.tracking_device === TrackingDevice.TrajektVision;

        const lastCompletedVcc = onVision
          ? await VisionCameraCalibrationsService.getInstance().getLastCompleted(
              machineDetails.machineID
            )
          : undefined;

        set((prev) => ({
          // might as well also update the machines list with the fetched details
          machines: prev.machines.map((m) =>
            m.machineID === machineDetails.machineID ? machineDetails : m
          ),
          machine: machineDetails,
          lastCompletedRT: lastCompletedRT,
          lastCompletedVcc: lastCompletedVcc,

          serviceLogs: serviceLogs,
        }));
      }

      return !!machineDetails;
    },

    setServiceLogs: (logs) => set({ serviceLogs: logs }),

    setLastMachineStatus: (status) => {
      set({
        lastMachineStatus: status,
      });
    },
  };
};
